import React from "react";

import { faInfoCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import { useID } from "~/lib/hooks";
import ArrowDropdown from "~/ui/ArrowDropdown";
import Checkbox from "~/ui/checkbox/Checkbox";
import { Switch } from "~/ui/legacyFellowIndex";
import Tooltip from "~/ui/Tooltip";

import { deleteTask, getTimeText, Header, saveTask, ValueDropdownItem } from "./Tasks";
import { NOTIFICATION_TARGET_VALUES, type NotificationTargetValueType, TARGET_TEXT_MAP, type TaskType } from "./types";

import styles from "./TimeSaver.module.css";

export const TIME_SAVER_VALUES = [-5, -15, -30, -60, -90, -120, -180, -360, -720, -1440, -2160] as const; // minutes
export type TimeSaverValueType = (typeof TIME_SAVER_VALUES)[number];

function CollapsedBox({
	title,
	checked,
	onChange,
}: {
	title: string;
	checked: boolean;
	onChange: (value: boolean) => void;
}) {
	const toggleID = useID();
	return (
		<div className={cx(styles.box, styles.collapsed)}>
			<div className={styles.row}>
				<label htmlFor={toggleID} className={styles.label}>
					<b>{title}</b>
				</label>
				<Switch id={toggleID} size="small" checked={checked} onCheckedChange={onChange} />
			</div>
		</div>
	);
}

interface ReminderCancellationProps {
	// Common props between Reminder & Cancellation
	isActive: boolean;
	delta: TimeSaverValueType;
	target: NotificationTargetValueType;
	// callbacks
	onChangeIsActive: (value: boolean) => void;
	onChangeDelta: (value: TimeSaverValueType) => void;
	onChangeTarget: (value: NotificationTargetValueType) => void;
}

type CancellationProps = ReminderCancellationProps & { minDelta?: TimeSaverValueType };

function Cancellation({
	isActive,
	delta,
	minDelta,
	target,
	// callbacks
	onChangeIsActive,
	onChangeDelta,
	onChangeTarget,
}: CancellationProps) {
	const toggleID = useID();

	if (!isActive) {
		return <CollapsedBox title="Cancellation" checked={isActive} onChange={onChangeIsActive} />;
	}

	return (
		<div className={styles.box}>
			<div className={styles.row}>
				<label htmlFor={toggleID} className={styles.label}>
					<b>Cancellation</b>
				</label>
				<Switch id={toggleID} size="small" checked={isActive} onCheckedChange={onChangeIsActive} />
			</div>
			<div className={styles.row}>
				<label className={styles.label}>
					Fellow will attempt to cancel this meeting
					<Tooltip
						className={styles.tooltip}
						content="Requires the organizer to have their calendar connected to this Fellow workspace"
					>
						<FontAwesomeIcon icon={faInfoCircle} className={styles.infoIcon} />
					</Tooltip>
				</label>
			</div>
			<div className={styles.row}>
				<label className={styles.label}>Check the agenda:</label>
				<ArrowDropdown
					forceCloseOnOutsideClick
					compact
					heading="Check the agenda"
					text={getTimeText(delta)}
					className={styles.dropdown}
					textClassName={styles.dropdownText}
					popupClassName={styles.dropdownPopup}
				>
					{TIME_SAVER_VALUES.slice(0, -1).map(value => (
						<ValueDropdownItem
							key={value}
							value={value}
							onClick={onChangeDelta}
							disabledHelpText={
								minDelta && value <= minDelta ? "Cancellation must be scheduled after the reminder" : undefined
							}
						>
							{getTimeText(value)}
						</ValueDropdownItem>
					))}
				</ArrowDropdown>
			</div>

			<div className={styles.row}>
				<label className={styles.label}>
					Send cancellation to:
					<Tooltip className={styles.tooltip} content="Cancellation will be sent via email and push notifications">
						<FontAwesomeIcon icon={faInfoCircle} className={styles.infoIcon} />
					</Tooltip>
				</label>
				<ArrowDropdown
					forceCloseOnOutsideClick
					compact
					heading="Send a notification to"
					text={TARGET_TEXT_MAP[target]}
					className={styles.dropdown}
					textClassName={styles.dropdownText}
					popupClassName={styles.dropdownPopup}
				>
					{NOTIFICATION_TARGET_VALUES.map(value => (
						<ValueDropdownItem key={value} value={value} onClick={onChangeTarget}>
							{TARGET_TEXT_MAP[value]}
						</ValueDropdownItem>
					))}
				</ArrowDropdown>
			</div>
		</div>
	);
}

type ReminderProps = ReminderCancellationProps & {
	maxDelta?: TimeSaverValueType;
	addBanner: boolean;
	// callbacks
	onChangeAddBanner: (value: boolean) => void;
};

function Reminder({
	isActive,
	delta,
	maxDelta,
	addBanner,
	target,
	// callbacks
	onChangeIsActive,
	onChangeDelta,
	onChangeAddBanner,
	onChangeTarget,
}: ReminderProps) {
	const toggleID = useID();
	const checkboxID = useID();

	if (!isActive) {
		return <CollapsedBox title="Reminder" checked={isActive} onChange={onChangeIsActive} />;
	}

	return (
		<div className={styles.box}>
			<div className={styles.row}>
				<label htmlFor={toggleID} className={styles.label}>
					<b>Reminder</b>
				</label>
				<Switch id={toggleID} size="small" checked={isActive} onCheckedChange={onChangeIsActive} />
			</div>
			<div className={styles.row}>
				<label className={styles.label}>Check the agenda:</label>
				<ArrowDropdown
					forceCloseOnOutsideClick
					compact
					heading="Check the agenda"
					text={getTimeText(delta)}
					className={styles.dropdown}
					textClassName={styles.dropdownText}
					popupClassName={styles.dropdownPopup}
				>
					{TIME_SAVER_VALUES.slice(1).map(value => (
						<ValueDropdownItem
							key={value}
							value={value}
							onClick={onChangeDelta}
							disabledHelpText={
								maxDelta && value >= maxDelta ? "Reminder must be scheduled before the cancellation" : undefined
							}
						>
							{getTimeText(value)}
						</ValueDropdownItem>
					))}
				</ArrowDropdown>
			</div>

			<div className={styles.row}>
				<label className={styles.label}>
					Send reminder to:
					<Tooltip content="Reminder will be sent via email and push notifications" className={styles.tooltip}>
						<FontAwesomeIcon icon={faInfoCircle} className={styles.infoIcon} />
					</Tooltip>
				</label>
				<ArrowDropdown
					forceCloseOnOutsideClick
					compact
					heading="Send a notification to"
					text={TARGET_TEXT_MAP[target]}
					className={styles.dropdown}
					textClassName={styles.dropdownText}
					popupClassName={styles.dropdownPopup}
				>
					{NOTIFICATION_TARGET_VALUES.map(value => (
						<ValueDropdownItem key={value} value={value} onClick={onChangeTarget}>
							{TARGET_TEXT_MAP[value]}
						</ValueDropdownItem>
					))}
				</ArrowDropdown>
			</div>

			<div className={styles.row}>
				<Checkbox id={checkboxID} checked={addBanner} onCheckedChange={onChangeAddBanner} className={styles.checkbox} />
				<label htmlFor={checkboxID} className={styles.label}>
					Display a banner in the note to attendees suggesting the meeting be cancelled
				</label>
			</div>
		</div>
	);
}

interface TimeSaverProps {
	streamId: string;
	automationId: string;
	// task data
	tasks?: readonly TaskType[];
	// callbacks
	onBack: () => void;
}

export default function TimeSaver({
	streamId,
	automationId,
	// task data
	tasks = [],
	// callbacks
	onBack,
}: TimeSaverProps) {
	const [loading, setLoading] = React.useState<boolean>(false);

	// Cancellation data
	const cancellationTask = tasks.find(t => t.data?.type === "CANCELLATION");
	const cancellationInitialDelta = (cancellationTask?.delta ?? TIME_SAVER_VALUES[0]) as TimeSaverValueType; // minutes
	const cancellationInitialDeltaIndex = TIME_SAVER_VALUES.indexOf(cancellationInitialDelta);
	const cancellationInitialTarget = (cancellationTask?.data?.sendNotificationTo ??
		NOTIFICATION_TARGET_VALUES[0]) as NotificationTargetValueType;

	const [cancellationIsActive, setCancellationIsActive] = React.useState<boolean>(!!cancellationTask);
	const [cancellationDelta, _setCancellationDelta] = React.useState<TimeSaverValueType>(cancellationInitialDelta);
	const [cancellationTarget, setCancellationTarget] =
		React.useState<NotificationTargetValueType>(cancellationInitialTarget);

	// Reminder data
	const reminderTask = tasks.find(t => t.data?.type === "REMINDER");
	const reminderInitialDelta = (reminderTask?.delta ??
		TIME_SAVER_VALUES[cancellationInitialDeltaIndex + 1]) as TimeSaverValueType; // minutes
	const reminderInitialAddBanner = reminderTask?.data?.addBanner ?? true;
	const reminderInitialTarget = (reminderTask?.data?.sendNotificationTo ??
		NOTIFICATION_TARGET_VALUES[0]) as NotificationTargetValueType;

	const [reminderIsActive, setReminderIsActive] = React.useState<boolean>(!!reminderTask);
	const [reminderDelta, _setReminderDelta] = React.useState<TimeSaverValueType>(reminderInitialDelta);
	const [reminderAddBanner, setReminderAddBanner] = React.useState<boolean>(reminderInitialAddBanner);
	const [reminderTarget, setReminderTarget] = React.useState<NotificationTargetValueType>(reminderInitialTarget);

	// Tasks input
	const commonInput = React.useMemo(() => ({ streamId, automationId }), [streamId, automationId]);
	const reminderInput = React.useMemo(
		() => ({
			...commonInput,
			task: {
				id: reminderTask?.id,
				delta: reminderDelta,
				// eslint-disable-next-line camelcase
				data: { type: "REMINDER", add_banner: reminderAddBanner, send_notification_to: reminderTarget },
			},
		}),
		[commonInput, reminderTask, reminderDelta, reminderAddBanner, reminderTarget],
	);

	const cancellationInput = React.useMemo(
		() => ({
			...commonInput,
			task: {
				id: cancellationTask?.id,
				delta: cancellationDelta,
				// eslint-disable-next-line camelcase
				data: { type: "CANCELLATION", send_notification_to: cancellationTarget },
			},
		}),
		[commonInput, cancellationTask, cancellationDelta, cancellationTarget],
	);

	const fieldsChanged = Boolean(
		// Task is being created
		(!reminderTask && reminderIsActive) ||
			(!cancellationTask && cancellationIsActive) ||
			// Task existed, but we are deleting it
			(reminderTask && !reminderIsActive) ||
			(cancellationTask && !cancellationIsActive) ||
			// Task existed and we are updating it
			(reminderTask &&
				(reminderInitialDelta !== reminderDelta ||
					reminderInitialAddBanner !== reminderAddBanner ||
					reminderInitialTarget !== reminderTarget)) ||
			(cancellationTask &&
				(cancellationInitialDelta !== cancellationDelta || cancellationInitialTarget !== cancellationTarget)),
	);

	const onComplete = React.useCallback(
		(success = true) => {
			setLoading(false);

			if (success) {
				onBack?.();
			}
		},
		[onBack],
	);

	const onSave = React.useCallback(() => {
		if (loading) return;

		setLoading(true);

		const callback = cancellationIsActive || cancellationTask ? undefined : onComplete;
		if (reminderIsActive) {
			saveTask(reminderInput, "Reminder", callback);
		} else if (reminderTask) {
			// We delete this task only if it existed before
			deleteTask(reminderTask.id, "Reminder", callback);
		}

		if (cancellationIsActive) {
			saveTask(cancellationInput, "Cancellation", onComplete);
		} else if (cancellationTask) {
			// We delete this task only if it existed before
			deleteTask(cancellationTask.id, "Cancellation", onComplete);
		}
	}, [
		loading,
		reminderIsActive,
		reminderInput,
		reminderTask,
		cancellationIsActive,
		cancellationInput,
		cancellationTask,
		onComplete,
	]);

	const setReminderDelta = React.useCallback(
		(value: TimeSaverValueType) => {
			_setReminderDelta(value);

			if (!cancellationIsActive) {
				const reminderDeltaIndex = TIME_SAVER_VALUES.indexOf(value);
				const cancellationDeltaIndex = TIME_SAVER_VALUES.indexOf(cancellationDelta);

				if (reminderDeltaIndex <= cancellationDeltaIndex) {
					// We need to update Reminder delta value to match the restrictions
					// we have: Reminder must be scheduled before the cancellation
					_setCancellationDelta(TIME_SAVER_VALUES[reminderDeltaIndex - 1]);
				}
			}
		},
		[cancellationIsActive, cancellationDelta],
	);

	const setCancellationDelta = React.useCallback(
		(value: TimeSaverValueType) => {
			_setCancellationDelta(value);

			if (!reminderIsActive) {
				const reminderDeltaIndex = TIME_SAVER_VALUES.indexOf(reminderDelta);
				const cancellationDeltaIndex = TIME_SAVER_VALUES.indexOf(value);

				if (reminderDeltaIndex <= cancellationDeltaIndex) {
					// We need to update Reminder delta value to match the restrictions
					// we have: Cancellation must be scheduled after the reminder
					_setReminderDelta(TIME_SAVER_VALUES[cancellationDeltaIndex + 1]);
				}
			}
		},
		[reminderIsActive, reminderDelta],
	);

	return (
		<>
			<Header
				name="No agenda"
				onHome={onBack}
				onBack={onBack}
				showButtons
				// as input == undefined, we will disable the save button when onCustomSave == undefined
				onCustomSave={fieldsChanged && !loading ? onSave : undefined}
			/>

			<div className={cx(styles.box, styles.tight)}>
				<p className={styles.title}>If there is no agenda…</p>
			</div>

			<Reminder
				isActive={reminderIsActive}
				delta={reminderDelta}
				maxDelta={cancellationIsActive ? cancellationDelta : undefined}
				addBanner={reminderAddBanner}
				target={reminderTarget}
				onChangeIsActive={setReminderIsActive}
				onChangeDelta={setReminderDelta}
				onChangeAddBanner={setReminderAddBanner}
				onChangeTarget={setReminderTarget}
			/>

			<Cancellation
				isActive={cancellationIsActive}
				delta={cancellationDelta}
				minDelta={reminderIsActive ? reminderDelta : undefined}
				target={cancellationTarget}
				onChangeIsActive={setCancellationIsActive}
				onChangeDelta={setCancellationDelta}
				onChangeTarget={setCancellationTarget}
			/>
		</>
	);
}
